import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import useTranslations from "@/shared/lang/useTranslations";

import { USER_LANGUAGE_OPTIONS } from "../constants/users.constants";

export interface UserDetailFieldsProps {
  disabled: boolean;
}

/**
 * The editable profile fields, bound to React Hook Form through the enclosing `ColocaForm`.
 *
 * The `Coloca*Field` components pull value, error and aria wiring from the form context, which is also what
 * gives every input a label association — a previous hand-rolled version paired a bare `<label>` with an
 * input carrying no `id`, so a screen reader announced five unlabeled edit fields.
 *
 * Two fields carry a consequence the operator has to see before acting: changing the email clears its
 * verification server-side, and emptying an optional field CLEARS it (the payload sends `null`, never `""`,
 * which the API rejects).
 *
 * The schema lives in `utils/userProfileForm` rather than here, so this file exports only a component and
 * Fast Refresh keeps working.
 */
export function UserDetailFields({ disabled }: UserDetailFieldsProps) {
  const { t } = useTranslations("users");

  return (
    // `items-start` is load-bearing, not cosmetic: a `FormItem` is a grid of auto rows, so a stretched cell
    // redistributes its free space between label and input. Without it, the phone field's helper text makes
    // its row taller and the language select beside it drifts downward — the exact misalignment the create
    // drawer had.
    <div className="grid items-start gap-4 pt-2 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <ColocaInputField name="full_name" label={t("create_field_full_name")} disabled={disabled} />
      </div>

      <div className="sm:col-span-2">
        <ColocaInputField
          name="email"
          type="email"
          label={t("create_field_email")}
          description={t("detail_email_warning")}
          disabled={disabled}
        />
      </div>

      <ColocaInputField
        name="phone_number"
        label={t("create_field_phone")}
        description={t("detail_phone_hint")}
        inputMode="tel"
        disabled={disabled}
      />

      <ColocaSelectField
        name="preferred_language"
        label={t("create_field_language")}
        items={[...USER_LANGUAGE_OPTIONS]}
        disabled={disabled}
      />

      <div className="sm:col-span-2">
        <ColocaInputField
          name="country"
          label={t("create_field_country")}
          maxLength={100}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
